import { z } from "zod";
import { isoTimestampSchema } from "./identifiers.js";
/**
 * Provider and model choices (docs/product/ai-capacity/PROVIDERS-AND-MODELS.md).
 *
 * A person picks a provider and a model together in the Agent chat. The
 * provider of the picked model pays. An app's automatic tasks (automatic
 * repairs, scheduled Agent tasks and AI calls from the app's code) follow the
 * latest pick in its chat unless the app sets its own. Nothing ever falls back
 * to another provider: a choice that can't run fails and says why.
 */
/** The API that serves a model. The same model may be reachable through two. */
export const aiModelProviderSchema = z.enum(["openai", "anthropic", "openrouter"]);
/** A provider-native model id, e.g. `gpt-5.6-sol` or `moonshotai/kimi-k3`. */
export const aiNativeModelIdSchema = z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9][a-z0-9._:/-]*$/);
/**
 * `provider/model`, split at the first slash:
 * `openai/gpt-5.6-sol`, `openrouter/moonshotai/kimi-k3`.
 */
export const aiQualifiedModelIdSchema = z
    .string()
    .min(3)
    .max(220)
    .regex(/^(openai|anthropic|openrouter)\/[a-z0-9][a-z0-9._:/-]*$/);
export function qualifyAiModel(ref) {
    return `${ref.provider}/${ref.model}`;
}
export function parseQualifiedAiModel(value) {
    const parsed = aiQualifiedModelIdSchema.parse(value);
    const slash = parsed.indexOf("/");
    return {
        provider: aiModelProviderSchema.parse(parsed.slice(0, slash)),
        model: parsed.slice(slash + 1),
    };
}
/** A pick: one connected provider and one of its models, or trial credit. */
export const aiModelChoiceSchema = z.discriminatedUnion("source", [
    z
        .object({
        source: z.literal("connection"),
        connectionId: z.uuid(),
        model: aiQualifiedModelIdSchema,
    })
        .strict(),
    z
        .object({
        source: z.literal("included"),
        model: aiQualifiedModelIdSchema,
    })
        .strict(),
]);
export function sameAiModelChoice(left, right) {
    if (!left || !right || left.source !== right.source || left.model !== right.model)
        return false;
    return left.source === "included" || (right.source === "connection" && left.connectionId === right.connectionId);
}
/** Why a choice can't take new work now. */
export const aiModelProblemCodeSchema = z.enum([
    "connection_missing",
    "connection_unavailable",
    "out_of_credit",
    "rate_limited",
    "budget_reached",
    "model_off",
    "model_not_enabled",
    "no_engine",
    "trial_ended",
]);
export const aiModelProblemSchema = z
    .object({
    code: aiModelProblemCodeSchema,
    /** One plain sentence, e.g. "Acme OpenRouter key is out of credit". */
    message: z.string().trim().min(1).max(300),
})
    .strict();
/** USD nanodollars per million tokens, as decimal strings. */
export const aiModelPriceSchema = z
    .object({
    inputPerMillion: z.string().regex(/^(0|[1-9][0-9]{0,39})$/),
    outputPerMillion: z.string().regex(/^(0|[1-9][0-9]{0,39})$/),
})
    .strict();
export const aiModelPickerOptionSchema = z
    .object({
    choice: aiModelChoiceSchema,
    label: z.string().trim().min(1).max(200),
    vendor: z.string().trim().min(1).max(100),
    price: aiModelPriceSchema.nullable(),
    problem: aiModelProblemSchema.nullable(),
})
    .strict();
/** One connected provider (or trial credit) and the models switched on for it. */
export const aiModelPickerGroupSchema = z
    .object({
    /** The connection id, or `included` for trial credit. */
    id: z.union([z.uuid(), z.literal("included")]),
    label: z.string().trim().min(1).max(200),
    /** E.g. "OpenAI sign-in", "OpenRouter API key · $41.20 left", "Trial credit · $4.20 left". */
    detail: z.string().trim().min(1).max(200),
    provider: aiModelProviderSchema.nullable(),
    /** Whose connected account pays; no account identity or credentials. */
    accountOwner: z.enum(["you", "app_owner", "organisation"]).optional(),
    options: z.array(aiModelPickerOptionSchema).max(200),
})
    .strict();
export const aiChoiceLabelSchema = z
    .object({
    model: aiQualifiedModelIdSchema,
    modelLabel: z.string().trim().min(1).max(200),
    providerLabel: z.string().trim().min(1).max(200),
})
    .strict();
/** Where an app's automatic tasks get their choice from. */
export const aiAutomaticSourceSchema = z.enum(["override", "chat", "default"]);
export const aiAutomaticStatusSchema = z
    .object({
    source: aiAutomaticSourceSchema,
    /** Null only when nothing at all can be picked. */
    choice: aiModelChoiceSchema.nullable(),
    label: aiChoiceLabelSchema.nullable(),
    problem: aiModelProblemSchema.nullable(),
})
    .strict();
/**
 * `GET /v1/agent-models?appId=&threadId=` (Agent browser API): what the
 * composer offers this person, and where a chat starts before anyone picks.
 */
export const agentModelPickerSchema = z
    .object({
    schemaVersion: z.literal(2),
    groups: z.array(aiModelPickerGroupSchema).max(100),
    /** The person's last pick, else the harness default, else the first option that can run. */
    defaultChoice: aiModelChoiceSchema.nullable(),
    /** The thread's own pick, when it has one. */
    threadChoice: aiModelChoiceSchema.nullable(),
    /** Personal app admins choose for their messages, without changing the shared thread. */
    selectionScope: z.enum(["conversation", "participant"]).optional(),
    ownerSharingAllowed: z.boolean().optional(),
    reasoningEfforts: z
        .array(z.object({ id: z.string().min(1).max(20), label: z.string().min(1).max(100) }).strict())
        .max(10),
    defaultReasoningEffort: z.string().min(1).max(20),
    /** For an app chat: what its automatic tasks use now. Null outside apps. */
    automatic: aiAutomaticStatusSchema.nullable(),
    /** Whether the viewer may manage providers (link to AI providers vs Connect a provider). */
    canManageProviders: z.boolean(),
})
    .strict();
/**
 * An automatic task that couldn't run, posted to the app's canonical
 * conversation as a run-free system message (`messages.automatic_failure`).
 * Features failures are one message per cause, updated in place with a count.
 */
const automaticFailureBase = {
    schemaVersion: z.literal(1),
    episodeId: z.uuid(),
    choice: aiChoiceLabelSchema.nullable(),
    source: aiAutomaticSourceSchema,
    reason: aiModelProblemSchema,
};
export const agentAutomaticFailureSchema = z.discriminatedUnion("kind", [
    z
        .object({
        ...automaticFailureBase,
        kind: z.literal("repair"),
        /** What the repair answered, e.g. "The 18:00 daily summary job failed." */
        signal: z.string().trim().min(1).max(300),
        occurredAt: isoTimestampSchema,
    })
        .strict(),
    z
        .object({
        ...automaticFailureBase,
        kind: z.literal("task"),
        taskTitle: z.string().trim().min(1).max(200).nullable(),
        occurredAt: isoTimestampSchema,
    })
        .strict(),
    z
        .object({
        ...automaticFailureBase,
        kind: z.literal("features"),
        failedCalls: z.number().int().min(1),
        since: isoTimestampSchema,
        lastAt: isoTimestampSchema,
    })
        .strict(),
]);
/** The stand-alone sentence stored in the failure message's content. */
export function describeAgentAutomaticFailure(failure) {
    const model = failure.choice
        ? `${failure.choice.modelLabel} via ${failure.choice.providerLabel}`
        : "no model";
    switch (failure.kind) {
        case "repair":
            return `Automatic repair couldn't start. ${failure.signal} It needed ${model}: ${failure.reason.message}. No other provider was tried.`;
        case "task":
            return `Scheduled task${failure.taskTitle ? ` "${failure.taskTitle}"` : ""} couldn't start. It needed ${model}: ${failure.reason.message}. No other provider was tried.`;
        case "features":
            return `AI calls from the app's code are failing: ${failure.failedCalls} failed. They need ${model}: ${failure.reason.message}. No other provider was tried.`;
    }
}
//# sourceMappingURL=model-choices.js.map